import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronUp, Instagram } from 'lucide-react';
import { Logo } from './icons/Logo';
import { useAffiliate } from '../hooks/useAffiliate';
import { trackPixelEvent } from '../utils/pixel';
import { DEFAULT_FOOTER_LINKS } from '../utils/menuDefaults';
import { AdminCategory, StoreConfig, db } from '../services/backend';
import { CONTACT_PHONE, WHATSAPP_NUMBER } from '../../constants';

interface FooterProps {
  config?: StoreConfig['banners']['footer'];
  categories?: AdminCategory[];
}

type FooterLink = {
  label: string;
  url: string;
  order?: number;
};

const PAYMENT_METHODS = ['Visa', 'Mastercard', 'Elo', 'Amex', 'Pix', 'Boleto'];

const DEFAULT_LINKS: FooterLink[] = DEFAULT_FOOTER_LINKS;

const PAGE_ROUTE_BY_SLUG: Record<string, string> = {
  sobre: '/sobre',
  contato: '/contato',
  rastreio: '/rastreio',
  trocas: '/trocas',
  'trocas-e-devolucoes': '/trocas',
  'seja-um-revendedor': '/seja-um-revendedor',
  'politica-de-privacidade': '/politica-de-privacidade',
  blog: '/blog',
};

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') && trimmed.length > 1 ? trimmed.slice(0, -1) : trimmed;
};

const dedupeFooterLinks = (links: FooterLink[]) => {
  const unique = new Map<string, FooterLink>();

  links.forEach((link, index) => {
    const normalizedUrl = normalizeUrl(link.url);
    const normalizedLabel = link.label.trim().toLowerCase();
    if (!normalizedUrl || !normalizedLabel) return;

    const key = `${normalizedUrl}::${normalizedLabel}`;
    if (!unique.has(key)) {
      unique.set(key, {
        label: link.label.trim(),
        url: normalizedUrl,
        order: link.order ?? 1000 + index,
      });
    }
  });

  return Array.from(unique.values()).sort((left, right) => {
    const orderDiff = (left.order ?? 9999) - (right.order ?? 9999);
    if (orderDiff !== 0) return orderDiff;
    return left.label.localeCompare(right.label, 'pt-BR');
  });
};

const isExternalUrl = (value: string) =>
  /^https?:\/\//i.test(value) || /^mailto:/i.test(value) || /^tel:/i.test(value);

const Footer: React.FC<FooterProps> = () => {
  const navigate = useNavigate();
  const affiliate = useAffiliate();
  const [institutionalLinks, setInstitutionalLinks] = useState<FooterLink[]>([]);
  const [storeConfig, setStoreConfig] = useState<StoreConfig | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [config, menus, pages] = await Promise.all([
          db.getStoreConfig(),
          db.getMenus(),
          db.getPages(),
        ]);

        setStoreConfig(config);

        const footerMenus: FooterLink[] = menus
          .filter((menu) => menu.type === 'footer' || menu.type === 'both')
          .sort((left, right) => left.order - right.order)
          .map((menu) => ({
            label: menu.label,
            url: menu.url,
            order: menu.order,
          }));

        const footerPages: FooterLink[] = pages
          .filter((page) => page.location === 'footer')
          .map((page, index) => ({
            label: page.title,
            url: PAGE_ROUTE_BY_SLUG[page.slug] ?? `/paginas/${page.slug}`,
            order: 1000 + index,
          }));

        const mergedLinks = dedupeFooterLinks([...footerMenus, ...footerPages]);
        setInstitutionalLinks(mergedLinks);
      } catch (error) {
        console.warn('Footer: failed to load links from Firestore.', error);
      }
    };

    loadData();
  }, []);

  const footerLinks = institutionalLinks.length > 0 ? institutionalLinks : DEFAULT_LINKS;
  const midpoint = Math.ceil(footerLinks.length / 2);
  const linkColumns = useMemo(
    () => [footerLinks.slice(0, midpoint), footerLinks.slice(midpoint)].filter((column) => column.length > 0),
    [footerLinks, midpoint],
  );

  const handleNavClick = (path: string) => {
    if (isExternalUrl(path)) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleContactClick = (type: 'whatsapp' | 'email' | 'phone') => {
    trackPixelEvent('Contact', { type });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const whatsappNumber = storeConfig?.contacts?.whatsapp || WHATSAPP_NUMBER;
  const whatsappDigits = whatsappNumber.replace(/\D/g, '');
  const whatsappLabel =
    whatsappDigits.length >= 13
      ? `(${whatsappDigits.slice(2, 4)}) ${whatsappDigits.slice(4, 9)}-${whatsappDigits.slice(9, 13)}`
      : whatsappDigits.length === 12
        ? `+${whatsappDigits.slice(0, 2)} ${whatsappDigits.slice(2, 4)} ${whatsappDigits.slice(4, 8)}-${whatsappDigits.slice(8)}`
        : CONTACT_PHONE;
  const emailAddress = storeConfig?.contacts?.email || 'verazbrasil@gmail.com';
  const footerLocation = storeConfig?.contacts?.address || 'Aparecida de Goiânia - GO';

  return (
    <footer className="relative bg-veraz-pink text-white">
      <div className="mx-auto max-w-[1440px] px-6 pb-8 pt-16 md:pt-20 lg:px-12">
        <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-3">
          <div>
            <Logo aria-label="Veraz Cosméticos" className="mb-6 h-auto w-[140px] text-white" />

            <p className="mb-4 text-[11px] uppercase tracking-[0.15em] text-white">
              Links rápidos
            </p>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {linkColumns.map((column, columnIndex) => (
                <ul key={`footer-column-${columnIndex}`} className="space-y-2.5">
                  {column.map((link) => (
                    <li key={`${link.url}-${link.label}`}>
                      <button
                        onClick={() => handleNavClick(link.url)}
                        className="group flex items-center gap-2 text-left text-[13px] text-white/80 transition-colors hover:text-white"
                      >
                        <ChevronRight
                          size={12}
                          className="opacity-50 transition-opacity group-hover:opacity-100"
                        />
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.instagram.com/verazbrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 transition-all hover:bg-white hover:text-veraz-pink"
                title="Instagram"
              >
                <Instagram size={16} />
              </a>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <button
              onClick={scrollToTop}
              className="mb-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 transition-all hover:bg-white hover:text-veraz-pink"
              aria-label="Voltar ao topo"
            >
              <ChevronUp size={18} />
            </button>

            <div className="space-y-2 text-center">
              <p className="mb-3 text-[11px] uppercase tracking-[0.15em] text-white">contato</p>
              <a
                href={`https://wa.me/${whatsappDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleContactClick('whatsapp')}
                className="block text-[13px] text-white/80 transition-colors hover:text-white"
              >
                WhatsApp: {whatsappLabel}
              </a>
              <a
                href={`mailto:${emailAddress}`}
                onClick={() => handleContactClick('email')}
                className="block text-[13px] text-white/80 transition-colors hover:text-white"
              >
                {emailAddress}
              </a>
              <p className="block text-[13px] text-white/65">{footerLocation}</p>
            </div>
          </div>

          <div className="flex flex-col items-start md:items-end">
            <p className="mb-4 text-[11px] uppercase tracking-[0.15em] text-white">
              formas de pagamento
            </p>
            <div className="flex flex-wrap gap-2 md:justify-end">
              {PAYMENT_METHODS.map((method) => (
                <div
                  key={method}
                  className="rounded border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-medium text-white/80"
                >
                  {method}
                </div>
              ))}
            </div>

            {affiliate ? (
              <div className="mt-6 flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[11px] font-medium text-white">
                <span className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
                Apoiando: {affiliate.name || affiliate.affiliateCode}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="border-t border-white/15 py-4">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between px-6 text-[10px] text-white/40 md:flex-row lg:px-12">
          <p>© Veraz Cosméticos - {new Date().getFullYear()}. Todos os direitos reservados.</p>
          <p className="mt-1 md:mt-0">{footerLocation}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
